import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import { BUSINESS, CONTACT, SERVICES } from "@/data/content";
import { Placeholder } from "@/components/Placeholder";

const COMPANY_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/process", label: "Our Process" },
  { to: "/pricing", label: "Pricing & Packages" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact / Request a Quote" },
];

const LEGAL_LINKS = [
  { to: "/terms-and-conditions", label: "Terms & Conditions" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/refund-policy", label: "Refund & Cancellation Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
  { to: "/payment-information", label: "Payment Information" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Column = ({ title, links, prefix }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
    <ul className="mt-5 space-y-3 text-sm">
      {links.map((l) => (
        <li key={l.to}>
          <Link to={l.to} data-testid={`${prefix}-${slug(l.label)}`} className="text-slate-300 transition-colors duration-200 hover:text-white">
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Row = ({ icon: Icon, children, testId }) => (
  <div className="flex items-start gap-3 text-sm text-slate-300">
    <Icon size={16} className="mt-0.5 shrink-0 text-brand-light" />
    <span data-testid={testId}>{children}</span>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-ink text-white">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white font-display text-sm font-bold text-ink">MG</span>
            <span className="font-display text-xl font-bold tracking-tight">My Geeks LLC</span>
          </div>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
            Business management and website development services for small and mid-sized businesses. Scoped in writing, priced by quotation.
          </p>
          <div className="mt-8 space-y-3">
            <Row icon={Mail} testId="footer-email-row">
              <a data-testid="footer-email-link" href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-white">{CONTACT.email}</a>
            </Row>
            <Row icon={Phone} testId="footer-phone-row">
              <a data-testid="footer-phone-link" href={CONTACT.phoneHref} className="transition-colors hover:text-white">{CONTACT.phone}</a>
            </Row>
            <Row icon={MapPin} testId="footer-address">{CONTACT.address}</Row>
            <Row icon={Clock} testId="footer-hours"><Placeholder field={BUSINESS.hours} /></Row>
          </div>
          <div data-testid="footer-legal-name" className="mt-6 space-y-2 text-xs leading-relaxed text-slate-400">
            <p data-testid="footer-transparency">My Geeks LLC is a registered business management and web development firm registered in the State Wyoming, USA.</p>
            <p data-testid="footer-entity">Legal entity: <Placeholder field={BUSINESS.legalName} /> · <Placeholder field={BUSINESS.businessType} /> · EIN <span data-testid="footer-ein">{BUSINESS.ein.value}</span></p>
          </div>
          <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
            <iframe
              data-testid="footer-map"
              title="Map to the My Geeks LLC office in Sheridan, Wyoming"
              src="https://www.google.com/maps?q=1095+Sugarview+Dr+Ste+100+Sheridan+WY+82801&output=embed"
              className="h-44 w-full"
              style={{ filter: "grayscale(0.6) contrast(0.95)" }}
              loading="lazy"
            />
          </div>
        </div>

        <div className="grid gap-10 sm:grid-cols-3 lg:col-span-7 lg:pl-8">
          <Column title="Services" prefix="footer-service" links={SERVICES.map((s) => ({ to: s.path, label: s.title }))} />
          <Column title="Company" prefix="footer-link" links={COMPANY_LINKS} />
          <Column title="Policies" prefix="footer-legal" links={LEGAL_LINKS} />
        </div>
      </div>

      <div className="mt-14 border-t border-white/10 pt-6">
        <p data-testid="footer-provider-statement" className="text-sm text-slate-300">
          Services are provided by My Geeks LLC, a company registered in Wyoming USA.
        </p>
        <p data-testid="footer-copyright" className="mt-4 text-xs leading-relaxed text-slate-500">
          © {new Date().getFullYear()} My Geeks LLC. All rights reserved. My Geeks LLC is a registered corporate consultancy providing custom Software development, digital marketing, and business management services. Registered in the State Wyoming, USA.
        </p>
        <p className="mt-2 text-xs text-slate-500">{CONTACT.address} · {CONTACT.country} · EIN {BUSINESS.ein.value}</p>
      </div>
    </div>
  </footer>
);
